/**
 * `useStrategyGenerationJob` — async-aware strategy generation hook
 * for the StrategyPage UI surface.
 *
 * Task 085: the `generate-client-strategy-ai` edge function returns
 * a `job_id` immediately and runs the actual worker in a background
 * task that completes 3-6 minutes later. This hook encapsulates the
 * three lifecycle phases:
 *
 *   1. `start(clientId)` — POST the start endpoint, capture the
 *      job_id, and begin polling.
 *   2. polling — every 5-10 seconds, GET the status endpoint until
 *      the job reaches a terminal state.
 *   3. terminal — `succeeded` triggers `onSuccess` (the page bumps
 *      its reload key); `failed` populates `errorMessage` for the UI
 *      retry button.
 *
 * The hook also handles two edge cases:
 *   - `already_running`: the start endpoint can return an existing
 *     job_id when the user double-clicks. The hook latches onto that
 *     job and polls it instead of starting a new one.
 *   - `recently_succeeded`: when a previous run finished within the
 *     last 60 s, the start endpoint returns the existing terminal
 *     row. The hook treats that as immediate success.
 *
 * The hook also recovers in-flight jobs on page load via
 * `recoverActiveJob(clientId)` — useful when the user reloads the
 * page mid-generation. The polling loop attaches to whatever job is
 * currently `queued` or `running` for the client.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isApiError, type KmiApiError } from '../../api/client';
import { generateClientStrategyAi, getClientStrategyGenerationStatus } from '../../api/endpoints';
import type { StrategyGenerationJobRow, StrategyGenerationJobStatus } from '../../api/types';
import { isStrategyReadinessFieldKey, readinessFieldLabel } from './strategyReadiness';

const POLL_INTERVAL_MS = 7_000;
const DEFAULT_ESTIMATED_SECONDS = 240;

export type StrategyGenerationPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'starting' }
  | {
      readonly kind: 'tracking';
      readonly jobId: string;
      readonly status: StrategyGenerationJobStatus;
      readonly startedAt: string;
      readonly estimatedSeconds: number;
    }
  | {
      readonly kind: 'success';
      readonly jobId: string | null;
      readonly strategyVersionId: string | null;
    }
  | {
      readonly kind: 'failure';
      readonly jobId: string | null;
      readonly errorCode: string | null;
      readonly errorMessage: string;
    };

export interface UseStrategyGenerationJobOptions {
  readonly clientId: string;
  /** Called when the run reaches `status === 'succeeded'`. */
  readonly onSuccess: () => void;
}

export interface UseStrategyGenerationJobResult {
  readonly phase: StrategyGenerationPhase;
  readonly isBusy: boolean;
  readonly start: () => Promise<void>;
  readonly reset: () => void;
}

const TERMINAL_STATUSES: ReadonlySet<StrategyGenerationJobStatus> = new Set([
  'succeeded',
  'failed',
]);

export function useStrategyGenerationJob(
  options: UseStrategyGenerationJobOptions,
): UseStrategyGenerationJobResult {
  const { clientId, onSuccess } = options;
  const [phase, setPhase] = useState<StrategyGenerationPhase>({ kind: 'idle' });
  // Latest onSuccess in a ref so the polling loop doesn't restart
  // when the parent passes a fresh callback identity each render.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  // Pinned mutable refs so the polling effect can read fresh state
  // without taking a re-render dependency.
  const trackedJobIdRef = useRef<string | null>(null);

  // Recover an in-flight job on first mount / when client changes —
  // covers the "user reloaded the page mid-generation" case.
  useEffect(() => {
    let cancelled = false;
    // Task 274 — clear any stale terminal phase (e.g. a `failure` left from a
    // previous client, or a readiness gap that has since been fixed) on fresh
    // mount / client change, so an old error never lingers over a client whose
    // data is now valid. The recovery probe below re-attaches to a genuinely
    // in-flight job; `start()` clears `failure` on its own (sets `starting`).
    trackedJobIdRef.current = null;
    setPhase({ kind: 'idle' });
    if (!clientId) return;
    // Fire-and-forget: recovery is best-effort, the unmount cleanup
    // below handles the cancelled flag. `void` makes the
    // no-floating-promise lint rule happy while documenting intent.
    void getClientStrategyGenerationStatus({ client_id: clientId })
      .then((data) => {
        if (cancelled) return;
        const job = data.job;
        if (!job) return;
        if (job.status === 'queued' || job.status === 'running') {
          trackedJobIdRef.current = job.id;
          setPhase({
            kind: 'tracking',
            jobId: job.id,
            status: job.status,
            startedAt: job.started_at ?? job.created_at,
            estimatedSeconds: data.estimated_seconds || DEFAULT_ESTIMATED_SECONDS,
          });
        }
      })
      .catch(() => {
        // Recovery is best-effort. A 404 / network blip just leaves
        // the hook in `idle` until the user clicks generate.
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Polling loop. Only runs while phase === 'tracking'.
  useEffect(() => {
    if (phase.kind !== 'tracking') return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await getClientStrategyGenerationStatus({ job_id: phase.jobId });
        if (cancelled) return;
        const job = data.job;
        if (!job) {
          // Job vanished (e.g. RLS strip after admin reassignment).
          setPhase({
            kind: 'failure',
            jobId: phase.jobId,
            errorCode: 'not_found',
            errorMessage: 'تعذر تتبع حالة التوليد. حاول مجدداً.',
          });
          return;
        }
        if (TERMINAL_STATUSES.has(job.status)) {
          handleTerminalJob(job);
          return;
        }
        // Still in flight; update status (queued → running) and
        // schedule the next tick.
        setPhase((prev) =>
          prev.kind === 'tracking' && prev.jobId === phase.jobId
            ? { ...prev, status: job.status, startedAt: job.started_at ?? prev.startedAt }
            : prev,
        );
        timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        // Transient network failure — keep polling. The user can
        // also click the retry button once we surface a failure.
        timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    const handleTerminalJob = (job: StrategyGenerationJobRow) => {
      trackedJobIdRef.current = null;
      if (job.status === 'succeeded') {
        setPhase({
          kind: 'success',
          jobId: job.id,
          strategyVersionId: job.strategy_version_id,
        });
        onSuccessRef.current();
      } else if (job.status === 'failed') {
        setPhase({
          kind: 'failure',
          jobId: job.id,
          errorCode: job.error_code,
          errorMessage: job.error_message ?? 'تعذر توليد الاستراتيجية. حاول مجدداً.',
        });
      }
    };

    timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [phase]);

  const start = useCallback(async () => {
    if (!clientId) return;
    if (phase.kind === 'starting' || phase.kind === 'tracking') return;
    setPhase({ kind: 'starting' });
    try {
      const start = await generateClientStrategyAi({
        input: { client_id: clientId, mode: 'new_version' },
      });
      if (start.status === 'recently_succeeded') {
        // Treat as immediate success — the previous run is fresh
        // enough that a re-poll is wasted work.
        setPhase({
          kind: 'success',
          jobId: start.job_id,
          strategyVersionId: null,
        });
        onSuccessRef.current();
        return;
      }
      trackedJobIdRef.current = start.job_id;
      setPhase({
        kind: 'tracking',
        jobId: start.job_id,
        status: start.status === 'already_running' ? 'running' : 'queued',
        startedAt: start.started_at,
        estimatedSeconds: start.estimated_seconds || DEFAULT_ESTIMATED_SECONDS,
      });
    } catch (err) {
      const errorMessage = mapStartErrorToArabic(err);
      setPhase({
        kind: 'failure',
        jobId: null,
        errorCode: isApiError(err) ? err.code : null,
        errorMessage,
      });
    }
  }, [clientId, phase.kind]);

  const reset = useCallback(() => {
    trackedJobIdRef.current = null;
    setPhase({ kind: 'idle' });
  }, []);

  const isBusy = phase.kind === 'starting' || phase.kind === 'tracking';

  return { phase, isBusy, start, reset };
}

function mapStartErrorToArabic(err: unknown): string {
  if (!isApiError(err)) return 'تعذّر توليد الاستراتيجية بالذكاء الاصطناعي. حاول مجدداً.';
  switch (err.code) {
    case 'permission_denied':
      return 'لا تملك صلاحية توليد الاستراتيجية بالذكاء الاصطناعي.';
    case 'conflict':
      return 'يوجد بالفعل توليد قيد التنفيذ لهذا العميل. حدّث الصفحة لمتابعة التقدم.';
    case 'validation_error':
      return mapValidationErrorToArabic(err);
    case 'not_found':
      return 'العميل غير متاح أو لا تملك وصولاً إليه.';
    default:
      return 'تعذّر توليد الاستراتيجية بالذكاء الاصطناعي. حاول مجدداً.';
  }
}

// Task 274 — a `validation_error` from the strategy start endpoint is
// field-aware. The EF's `enforceReadiness` rejects with a `fields` map keyed by
// readiness fields; translate those to Arabic field labels. For any other
// validation failure (or a body with no recognised readiness key) show a
// neutral validation message — never claim onboarding data is missing when it
// is not, and never leak the raw `KMI API … (HTTP …)` envelope.
function mapValidationErrorToArabic(err: KmiApiError): string {
  const fields = err.body.fields;
  if (fields) {
    const labels = Object.keys(fields).filter(isStrategyReadinessFieldKey).map(readinessFieldLabel);
    if (labels.length > 0) {
      return `بيانات التأهيل ناقصة: ${labels.join('، ')}.`;
    }
  }
  return 'تعذّر تشغيل التوليد: تحقّق من صحة البيانات ثم حدّث الصفحة وحاول مجدداً.';
}

// Task 165 convention — expose the pure error mapper for unit tests without
// widening the hook's public API.
export const _mapStartErrorToArabicForTests = mapStartErrorToArabic;
